import React, { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import toast from 'react-hot-toast'
import { add_to_card, add_to_wishlist, messageClear } from '../../store/reducers/cardReducer'
import {
  resolveDopingBoost,
  sourceFromPath,
  trackDopingClick,
  trackDopingEvent
} from '../../utilies/dopingTracking'
import { resolveImageUrl } from '../../utilies/imageUrl'
import ItemzoProductCard from './ItemzoProductCard'

const ShopProducts = ({ products, styles, source, gridClassName }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const listRef = useRef(null)

  const { userInfo } = useSelector((state) => state.auth)
  const { successMessage, errorMessage } = useSelector((state) => state.card)
  const trackingSource = source || sourceFromPath(location.pathname, location.search)
  const isGrid = styles === 'grid'

  useEffect(() => {
    if (successMessage) {
      toast.success('Məhsul səbətə əlavə olundu')
      dispatch(messageClear())
    }
    if (errorMessage) {
      toast.error(errorMessage || 'Nəsə düzgün getmədi.')
      dispatch(messageClear())
    }
  }, [successMessage, errorMessage, dispatch])

  useEffect(() => {
    const root = listRef.current
    if (!root || typeof IntersectionObserver === 'undefined') return

    const cards = Array.from(root.querySelectorAll('[data-doping-card="true"]'))
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        const element = entry.target
        observer.unobserve(element)
        trackDopingEvent({
          product: {
            _id: element.dataset.productId,
            activeBoosts: [element.dataset.boostType].filter(Boolean),
            activeBoostDetails: element.dataset.boostId ? [{ _id: element.dataset.boostId, type: element.dataset.boostType }] : []
          },
          eventType: 'impression',
          source: element.dataset.source
        })
      })
    }, { threshold: 0.45 })

    cards.forEach(card => observer.observe(card))
    return () => observer.disconnect()
  }, [products, trackingSource])

  const requireAuth = (purpose) => {
    if (userInfo) return true
    Swal.fire({
      icon: 'warning',
      title: 'Giriş tələb olunur',
      text: purpose,
      confirmButtonText: 'Giriş et',
      buttonsStyling: false,
      customClass: { confirmButton: 'my-confirm-btn' }
    }).then((result) => {
      if (result.isConfirmed) navigate('/login')
    })
    return false
  }

  const handleCart = (product) => {
    if (product.productType === 'physical') {
      navigate(`/product/details/${product.slug}`)
      return
    }
    if (!requireAuth('Səbətə əlavə etmək üçün əvvəlcə giriş edin')) return
    dispatch(add_to_card({ userId: userInfo.id, productId: product._id, quantity: 1 }))
  }

  const handleWishlist = (product) => {
    if (!requireAuth('Favori üçün əvvəlcə giriş edin')) return
    dispatch(add_to_wishlist({
      userId: userInfo.id,
      productId: product._id,
      name: product.name,
      price: product.price,
      image: resolveImageUrl(product.images?.[0]),
      discount: product.discount,
      rating: product.rating,
      slug: product.slug
    }))
  }

  return (
    <div
      ref={listRef}
      className={`w-full grid ${
        isGrid
          ? (gridClassName || 'grid-cols-5 xl:grid-cols-4 md:grid-cols-3 sm:grid-cols-2')
          : 'grid-cols-1'
      } gap-4`}
    >
      {products?.map((product, i) => {
        const boost = resolveDopingBoost(product, trackingSource)
        return (
          <ItemzoProductCard
            key={product._id || i}
            product={product}
            source={trackingSource}
            boost={boost}
            onWishlist={handleWishlist}
            onCart={handleCart}
            onTrack={trackDopingClick}
          />
        )
      })}
    </div>
  )
}

export default ShopProducts
